import { GetStaticPaths } from 'next'
import { getTheatricalRelease } from 'src/services/Theatrical'
import { TheatricalPayItForwardView } from 'src/views/PayItForward/TheatricalPayItForwardView'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext/PayItForwardOffersDataContext'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { getIndexForOfferThatImpactsClosestTo100People, getOffersForProject, Offer } from '@/services/PifService'
import { TheatricalReleaseObject, TheatricalReleaseRegion } from '@/types/codegen-federation'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  initialOfferIndex: number
  offers: Offer[]
  locale: string
  theatricalRelease: TheatricalReleaseObject
  theatricalSlug: string
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  theatricalRelease,
  theatricalSlug,
}) => {
  const { t } = useTranslate('pif')
  const theatricalName = theatricalRelease?.title

  return (
    <DefaultLayout
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
      isDarkMode={false}
    >
      <DatadogRumFull />
      <div className="bg-gray-100">
        <Seo
          {...getBasicPageMetaData({
            title: t('pifTheatricalTitle', 'Pay it Forward for {{theatricalName}} on Angel Studios', {
              theatricalName,
            }),
            description: t(
              'pifTheatricalDescription',
              'By Paying it Forward to {{theatricalName}}, you help audiences see this deeply moving and monumental movie for free. Help us get this movie to as many people as possible.',
              { theatricalName },
            ),
            cloudinaryImagePath: `/${theatricalRelease?.region?.seoImage}.webp`,
            cloudinaryTransformation: cloudinaryImageTransformation,
            locale,
            path: `${paths.payItForward.index}/${theatricalSlug}`,
          })}
        />
        <PayItForwardOfferDataProvider offers={offers}>
          <TheatricalPayItForwardView theatricalRelease={theatricalRelease} />
        </PayItForwardOfferDataProvider>
      </div>
    </DefaultLayout>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/_theatrical-pif/[slug]/index.tsx' },
  async ({ federationClient, locale, next404, params }) => {
    const slug = params?.slug as string
    const offers = await getOffersForProject({ projectSlug: slug }, federationClient)
    const initialOfferIndex = getIndexForOfferThatImpactsClosestTo100People(offers)

    const theatricalRelease = await getTheatricalRelease({ theatricalSlug: slug, client: federationClient })

    if (!theatricalRelease || !offers?.length) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    const cloudinaryImageTransformation = 'c_scale'

    return {
      props: {
        cloudinaryImageTransformation,
        initialOfferIndex,
        locale,
        offers,
        theatricalRelease,
        theatricalSlug: slug,
        ...(await loadTranslations(locale, ['common', 'pif', 'account'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return page
}

export default PayItForwardProjectCheckoutPage
